import { FC } from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Input,
  Field,
} from '@chakra-ui/react';
import { InbreedingThresholds } from '@/types/aiManagement';

interface ThresholdInputProps {
  thresholds: InbreedingThresholds;
  onChange: (thresholds: InbreedingThresholds) => void;
  isDisabled?: boolean;
}

/**
 * 近親係数閾値入力コンポーネント
 * 視覚的なゾーン表示と数値入力を組み合わせた非エンジニア向けUI
 */
export const ThresholdInput: FC<ThresholdInputProps> = ({
  thresholds,
  onChange,
  isDisabled = false,
}) => {
  const isValid =
    thresholds.safe < thresholds.caution && thresholds.caution < thresholds.warning;

  const handleChange = (key: keyof InbreedingThresholds, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      onChange({ ...thresholds, [key]: numValue });
    }
  };

  // ゾーンの幅を計算（視覚化用）
  const safeWidth = (thresholds.safe / 100) * 100;
  const cautionWidth = ((thresholds.caution - thresholds.safe) / 100) * 100;
  const warningWidth = ((thresholds.warning - thresholds.caution) / 100) * 100;
  const dangerWidth = ((100 - thresholds.warning) / 100) * 100;

  return (
    <VStack align="stretch" gap={4}>
      {/* 視覚的なゾーン表示 */}
      <Box>
        <Text fontSize="sm" color="gray.600" mb={2}>
          近親係数のリスクゾーン
        </Text>
        <HStack gap={0} h="40px" borderRadius="md" overflow="hidden">
          <Box
            bg="green.400"
            w={`${safeWidth}%`}
            h="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {safeWidth > 15 && (
              <Text fontSize="xs" color="white" fontWeight="medium">
                安全
              </Text>
            )}
          </Box>
          <Box
            bg="yellow.400"
            w={`${cautionWidth}%`}
            h="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {cautionWidth > 15 && (
              <Text fontSize="xs" color="gray.800" fontWeight="medium">
                注意
              </Text>
            )}
          </Box>
          <Box
            bg="orange.400"
            w={`${warningWidth}%`}
            h="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {warningWidth > 15 && (
              <Text fontSize="xs" color="white" fontWeight="medium">
                警告
              </Text>
            )}
          </Box>
          <Box
            bg="red.500"
            w={`${dangerWidth}%`}
            h="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {dangerWidth > 15 && (
              <Text fontSize="xs" color="white" fontWeight="medium">
                危険
              </Text>
            )}
          </Box>
        </HStack>
        <HStack justify="space-between" mt={1}>
          <Text fontSize="xs" color="gray.500">
            0%
          </Text>
          <Text fontSize="xs" color="gray.500">
            100%
          </Text>
        </HStack>
      </Box>

      {/* 数値入力フィールド */}
      <HStack gap={4}>
        <Field.Root flex={1}>
          <Field.Label fontSize="sm">
            安全ゾーン上限
          </Field.Label>
          <HStack>
            <Input
              type="number"
              value={thresholds.safe}
              onChange={(e) => handleChange('safe', e.target.value)}
              disabled={isDisabled}
              size="sm"
              step={0.25}
              min={0}
              max={100}
            />
            <Text fontSize="sm" color="gray.500">
              %
            </Text>
          </HStack>
          <Field.HelperText fontSize="xs">
            推奨: 6.25%
          </Field.HelperText>
        </Field.Root>

        <Field.Root flex={1}>
          <Field.Label fontSize="sm">
            注意ゾーン上限
          </Field.Label>
          <HStack>
            <Input
              type="number"
              value={thresholds.caution}
              onChange={(e) => handleChange('caution', e.target.value)}
              disabled={isDisabled}
              size="sm"
              step={0.25}
              min={0}
              max={100}
            />
            <Text fontSize="sm" color="gray.500">
              %
            </Text>
          </HStack>
          <Field.HelperText fontSize="xs">
            推奨: 12.5%
          </Field.HelperText>
        </Field.Root>

        <Field.Root flex={1}>
          <Field.Label fontSize="sm">
            警告ゾーン上限
          </Field.Label>
          <HStack>
            <Input
              type="number"
              value={thresholds.warning}
              onChange={(e) => handleChange('warning', e.target.value)}
              disabled={isDisabled}
              size="sm"
              step={0.25}
              min={0}
              max={100}
            />
            <Text fontSize="sm" color="gray.500">
              %
            </Text>
          </HStack>
          <Field.HelperText fontSize="xs">
            推奨: 25%
          </Field.HelperText>
        </Field.Root>
      </HStack>

      {/* バリデーションエラー表示 */}
      {!isValid && (
        <Text fontSize="sm" color="red.500">
          閾値は「安全 &lt; 注意 &lt; 警告」の順序で設定してください
        </Text>
      )}

      {/* 説明 */}
      <Box bg="gray.50" p={3} borderRadius="md">
        <Text fontSize="xs" color="gray.600">
          <Text as="span" fontWeight="bold">
            近親係数とは：
          </Text>
          両親の血縁関係の近さを示す値です。値が高いほど遺伝的多様性が低く、
          遺伝性疾患のリスクが高まる可能性があります。
        </Text>
      </Box>
    </VStack>
  );
};
